import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, History, Frown } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { battleApi } from '../services/battleApi'
import BattleCard from '../components/BattleCard'
import useAudioManager from '../../../hooks/useAudioManager'
import useAppStore from '../../../store/useAppStore'
import { hapticImpact, hapticSelectionChanged } from '../../../utils/haptic'

/**
 * BattleHistory - Battle History Page
 * Shows finished battles with filtering by "All Battles" and "My Battles"
 */
const BattleHistory = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { playSound } = useAudioManager({
    pick: { url: '/sounds/common/pick.mp3', volume: 0.5 }
  })

  // Состояние
  const [activeTab, setActiveTab] = useState('all') // 'all' | 'my'
  const [allBattles, setAllBattles] = useState([])
  const [myBattles, setMyBattles] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const requestId = useRef(0)

  // Функция загрузки истории баттлов
  const loadBattlesHistory = useCallback(async () => {
    const currentRequest = ++requestId.current
    setIsLoading(true)
    setError(null)
    if (isGuest) {
      setMyBattles([])
      setActiveTab('all')
    }

    try {
      // Загружаем обе истории параллельно
      const [allBattlesData, myBattlesData] = await Promise.all([
        battleApi.getAllBattlesHistory(),
        isGuest ? Promise.resolve([]) : battleApi.getMyBattlesHistory()
      ])

      if (currentRequest !== requestId.current) return
      setAllBattles(allBattlesData)
      setMyBattles(myBattlesData)
    } catch (err) {
      if (currentRequest !== requestId.current || err?.isHandledAuthError) return
      console.error('Load battles history error:', err)
      const errorKey = err.message === 'error backend' ? 'battle.joinServerError' : 'battle.history.error'
      setError(errorKey)
      toast.error(t(errorKey))
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false)
    }
  }, [isGuest, t])

  // Загрузка данных при монтировании
  useEffect(() => {
    loadBattlesHistory()
    return () => { requestId.current += 1 }
  }, [loadBattlesHistory])

  // Получаем текущий список баттлов в зависимости от активной вкладки
  const currentBattles = activeTab === 'all' ? allBattles : myBattles

  // Обработчик переключения вкладки
  const handleTabChange = (tab) => {
    if (isGuest && tab === 'my') return openLogin()
    playSound('pick')
    hapticSelectionChanged()
    setActiveTab(tab)
  }

  return (
    <div className="container mx-auto px-4 py-6 pb-24 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => {
            hapticImpact('light')
            navigate('/battles')
          }}
          className="p-2 hover:bg-surface rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        <div className="flex items-center gap-3">
          <History className="w-6 h-6 text-accent-primary" />
          <h1 className="text-2xl font-bold text-white">
            {t('battle.history.title', 'История баттлов')}
          </h1>
        </div>
      </div>

      {/* Табы фильтрации */}
      <div className="mb-6">
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => handleTabChange('all')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'all'
                ? 'bg-accent-primary text-dark-primary'
                : 'bg-surface text-text-secondary hover:bg-surface/80'
            }`}
          >
            {t('battle.history.allBattles', 'Все баттлы')}
          </button>
          <button
            onClick={() => handleTabChange('my')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'my'
                ? 'bg-accent-primary text-dark-primary'
                : 'bg-surface text-text-secondary hover:bg-surface/80'
            }`}
          >
            {t('battle.history.myBattles', 'Мои баттлы')}
          </button>
        </div>
      </div>

      {/* Loading состояние */}
      {isLoading && (
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div 
              key={index} 
              className="bg-surface rounded-lg p-4 border border-white border-opacity-10 animate-pulse"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-white bg-opacity-20 rounded w-3/4"></div>
                    <div className="h-3 bg-white bg-opacity-20 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Список баттлов */}
      {!isLoading && !error && currentBattles.length > 0 && (
        <div className="space-y-4">
          {currentBattles.map((battle) => (
            <BattleCard key={battle.id} battle={battle} hideFinishedStatus={true} />
          ))}
        </div>
      )}

      {/* Empty state for "All Battles" */}
      {!isLoading && !error && activeTab === 'all' && allBattles.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-16 h-16 mb-4 rounded-full bg-surface flex items-center justify-center">
            <History className="w-8 h-8 text-text-secondary" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('battle.history.noHistory', 'История пуста')}
          </h3>
          <p className="text-text-secondary text-center mb-6">
            {t('battle.history.noHistoryDesc', 'Завершенные баттлы появятся здесь')}
          </p>
        </div>
      )}

      {/* Empty state for "My Battles" */}
      {!isLoading && !error && activeTab === 'my' && myBattles.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-16 h-16 mb-4 rounded-full bg-surface flex items-center justify-center">
            <Frown className="w-8 h-8 text-text-secondary" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('battle.history.noMyBattles', 'Вы ещё не участвовали в баттлах')}
          </h3>
          <p className="text-text-secondary text-center mb-6">
            {t('battle.history.noMyBattlesDesc', 'Создайте или присоединитесь к баттлу, чтобы начать играть')}
          </p>
          <button
            onClick={() => navigate('/battles')}
            className="px-6 py-3 bg-accent-primary text-dark-primary rounded-lg font-semibold hover:bg-accent-secondary transition-colors"
          >
            {t('battle.history.goToBattles', 'К баттлам')}
          </button>
        </div>
      )}

      {/* Ошибка */}
      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-16 h-16 mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <Frown className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('battle.history.error', 'Ошибка загрузки')}
          </h3>
          <p className="text-text-secondary text-center mb-6">
            {t(error)}
          </p>
          <button
            onClick={loadBattlesHistory}
            className="px-6 py-3 bg-accent-primary text-dark-primary rounded-lg font-semibold hover:bg-accent-secondary transition-colors"
          >
            {t('battle.history.retry', 'Попробовать снова')}
          </button>
        </div>
      )}
    </div>
  )
}

export default BattleHistory
